<script>
import axios from 'axios';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import {mapGetters} from 'vuex';
import TrekLoading from '@/components/TrekLoading';
import Tabs from '@/components/tabs/Tabs';
import CartSummary from '@/components/cart/CartSummary';
import TabPane from '@/components/tabs/TabPane';
import FormGroup from '@/components/FormGroup';
import ShippingInfo from '@/components/checkout/ShippingInfo';
import Collapse from '@/components/Collapse';
import CheckoutGrid from '@/components/checkout/CheckoutGrid';
import CheckoutNavigation from '@/components/checkout/CheckoutNavigation';
import OrderDetails from '@/components/checkout/OrderDetails';
import {PdlCallout} from '@pedal/pdl-callout';
import TrekLink from '@/components/TrekLink';

export default {
  components: {
    TrekLoading,
    Tabs,
    CartSummary,
    TabPane,
    FormGroup,
    ShippingInfo,
    Collapse,
    CheckoutGrid,
    CheckoutNavigation,
    OrderDetails,
    PdlCallout,
    TrekLink,
  },

  props: {
    checkoutStep: {
      type: String,
      default: '',
      required: false,
    },
    orderCode: {
      type: String,
      default: '',
      required: false,
    },
    skuNotAvailableText: {
      type: String,
      default: '',
      required: false,
    },
    unavailableItems: {
      type: Array,
      default: () => [],
      required: false,
    },
    baseProperties: {
      type: Array,
      default: () => [],
      required: false,
    },
    bikesOrderCode: {
      type: String,
      default: '',
      required: false,
    },
    partsOrderCode: {
      type: String,
      default: '',
      required: false,
    },
    customWaterBottlesOrderCode: {
      type: String,
      default: '',
    },
    projectOneBikesOrderCode: {
      type: String,
      default: '',
    },
    projectOneWarrantyFramesetOrderCode: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      cartData: {
        bikes: null,
        parts: null,
        customWaterBottles: null,
        projectOneBikes: null,
        cartSummary: null,
      },
      calculatedFreight: {
        bikes: false,
        parts: false,
        customWaterBottles: false,
        projectOne: false,
      },
      loading: true,
      types: ['bikes', 'parts', 'customWaterBottles', 'projectOneBikes'],
      selectedTabName: '',
      showSaveWarning: false,
      showCartValidationWarning: false,
      portOfDeliveryMaxChars: 11,
      asyncOrderInterval: '',
      asyncOrderProcessing: false,
      orderTypeMapping: {},
      timeoutError: false,
      emptyReturnError: false,
      orderData: '',
      orderNumber: '',
      bikesOrderUpdate: this.bikesOrderCode ? this.bikesOrderCode : '',
      partsOrderUpdate: this.partsOrderCode ? this.partsOrderCode : '',
      customWaterBottlesOrderUpdate: this.customWaterBottlesOrderCode ? this.customWaterBottlesOrderCode : '',
      projectOneBikesOrderUpdate: this.projectOneBikesOrderCode ? this.projectOneBikesOrderCode : '',
      projectOneWarrantyFramesetOrderUpdate: this.projectOneWarrantyFramesetOrderCode
        ? this.projectOneWarrantyFramesetOrderCode
        : '',
    };
  },

  computed: {
    bikes() {
      return this.cartData.bikes;
    },

    parts() {
      return this.cartData.parts;
    },

    customWaterBottles() {
      return this.cartData.customWaterBottles;
    },

    projectOneBikes() {
      return this.cartData.projectOneBikes;
    },

    isReadyToSetMessages() {
      return (
        this.orderTypeMapping.bikes?.isInCartData === this.orderTypeMapping.bikes?.hasOrderUpdate &&
        this.orderTypeMapping.parts?.isInCartData === this.orderTypeMapping.parts?.hasOrderUpdate &&
        this.orderTypeMapping.customWaterBottles?.isInCartData ===
          this.orderTypeMapping.customWaterBottles?.hasOrderUpdate &&
        this.orderTypeMapping.projectOneBikes?.isInCartData === this.orderTypeMapping.bikes?.hasOrderUpdate
      );
    },

    portOfDeliveryCharsLeftBikes() {
      if (!this.cartData.bikes.orderDetails.portofDelivery) return this.portOfDeliveryMaxChars.toString();
      else {
        let charsLeft = this.portOfDeliveryMaxChars - this.cartData.bikes.orderDetails.portofDelivery.length;
        if (charsLeft < 0) return '0';
        else return charsLeft.toString();
      }
    },

    portOfDeliveryCharsLeftParts() {
      if (!this.cartData.parts || !this.cartData.parts.orderDetails.portofDelivery)
        return this.portOfDeliveryMaxChars.toString();
      else {
        let charsLeft = this.portOfDeliveryMaxChars - this.cartData.parts.orderDetails.portofDelivery.length;
        if (charsLeft < 0) return '0';
        else return charsLeft.toString();
      }
    },

    showReviewButton() {
      return this.cartData.bikes && this.cartData.parts;
    },

    tabSelected: {
      get() {
        let tabSelected;
        if (this.selectedTabName) {
          tabSelected = this.selectedTabName;
        } else if (!isEmpty(this.bikes)) {
          tabSelected = 'bikes';
        } else if (!isEmpty(this.parts)) {
          tabSelected = 'parts';
        } else if (!isEmpty(this.customWaterBottles)) {
          tabSelected = 'customWaterBottles';
        } else if (!isEmpty(this.projectOneBikes)) {
          tabSelected = 'projectOne';
        } else {
          tabSelected = 'bikes';
        }
        return tabSelected;
      },
      set(name) {
        this.selectedTabName = name;
        if (this.checkoutStep === 'confirmation') {
          this.setMessagesTabChange();
        }
      },
    },

    bikesOrderDetails() {
      if (this.cartData.bikes && this.cartData.bikes.orderDetails) {
        return get(this, 'cartData.bikes.orderDetails', {});
      }

      return {};
    },

    partsOrderDetails() {
      if (this.cartData.parts && this.cartData.parts.orderDetails) {
        return get(this, 'cartData.parts.orderDetails', {});
      }

      return {};
    },

    customWaterBottlesOrderDetails() {
      return this.cartData.customWaterBottles && this.cartData.customWaterBottles.orderDetails
        ? get(this, 'cartData.customWaterBottles.orderDetails', {})
        : {};
    },

    projectOneBikesOrderDetails() {
      return this.cartData.projectOneBikes && this.cartData.projectOneBikes.orderDetails
        ? get(this, 'cartData.projectOneBikes.orderDetails', {})
        : {};
    },

    endpoint() {
      switch (this.checkoutStep) {
        case 'shipments':
          return '/checkout-b2b/api/multi-step/shipmentsInfo?groupBy=warehouse&includes=parts,bikes,customWaterBottles,projectOne';
        case 'orderReview':
          return '/checkout-b2b/api/orderInfo';
        case 'confirmation':
          return `/checkout-b2b/api/order-review/orderSummaryInfo?orderCode=${this.orderCode}`;
        default:
          console.error('No checkout category provided');
          return undefined;
      }
    },

    summaryTotalItems() {
      const bikesTotal = parseInt(get(this.cartData, 'cartSummary.bikes.totalItems', 0)) || 0;
      const partsTotal = parseInt(get(this.cartData, 'cartSummary.parts.totalItems', 0)) || 0;
      const customWaterBottlesTotal = parseInt(get(this.cartData, 'cartSummary.customWaterBottles.totalItems', 0)) || 0;
      const projectOneBikesTotal = parseInt(get(this.cartData, 'cartSummary.projectOneBikes.totalItems', 0)) || 0;
      return bikesTotal + partsTotal + customWaterBottlesTotal + projectOneBikesTotal;
    },

    allocatedItemsExistenceMap() {
      let result = {};
      this.types.forEach((type) => {
        if (this.cartData[type] !== null) {
          result[type] = true;
          this.cartData[type].warehouses?.forEach((item) => {
            if (item.entries?.find((el) => el.isAllocated === false || el.isAllocated === null)) {
              result[type] = false;
            }
          });
        }
      });
      return result;
    },

    ...mapGetters('advancedOrdering', ['advancedOrderingMode', 'aoToggleCompletedWatcher']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  watch: {
    aoToggleCompletedWatcher() {
      this.loadCart();
    },
  },

  async mounted() {
    this.displayUnavailableItemsWarnings();
    await this.loadCart();
    if (this.checkoutStep === 'confirmation' && !this.orderData) {
      this.asyncOrderCheck();
    }
  },

  methods: {
    cartSummaryLoaded() {
      if (this.checkoutStep === 'shipments') {
        this.enableShipmentsSubmitListeners();
      }
    },

    validateCartData() {
      if (this.checkoutStep === 'orderReview') {
        this.performCartValidation();
      }
    },

    asyncOrderCheck() {
      const asyncPingInterval = 3000;
      const asyncPingTimeout = 60000;
      this.generateOrderTypeMapping();
      this.asyncOrderProcessing = true;
      this.getOrderNumber();
      this.asyncOrderInterval = setInterval(this.getOrderNumber, asyncPingInterval);
      setTimeout(() => {
        clearInterval(this.asyncOrderInterval);
        this.asyncOrderProcessing = false;
        this.setMessages();
        if (!this.orderData) this.timeoutError = true;
      }, asyncPingTimeout);
    },

    generateOrderTypeMapping() {
      this.orderTypeMapping = {
        bikes: {
          isInCartData: !!this.bikes,
          hasOrderUpdate: !!this.bikesOrderUpdate,
        },
        parts: {
          isInCartData: !!this.parts,
          hasOrderUpdate: !!this.partsOrderUpdate,
        },
        customWaterBottles: {
          isInCartData: !!this.customWaterBottles,
          hasOrderUpdate: !!this.customWaterBottlesOrderUpdate,
        },
        projectOneBikes: {
          isInCartData: !!this.projectOneBikes,
          hasOrderUpdate: !!this.projectOneBikesOrderUpdate,
        },
        projectOneWarrantyFrameset: {
          isInCartData: !!this.projectOneBikes,
          hasOrderUpdate: !!this.projectOneWarrantyFramesetOrderUpdate,
        },
      };
    },

    getOrderNumber() {
      if (this.isReadyToSetMessages) {
        this.setMessages();
        return;
      }
      axios
        .get(`/checkout-b2b/api/order-review/jdeOrderNumber?orderCode=${this.orderCode}`)
        .then(({data}) => {
          if (
            data.data.bikesOrderCode ||
            data.data.partsOrderCode ||
            data.data.customWaterBottlesOrderCode ||
            data.data.projectOneBikesOrderCode ||
            data.data.projectOneWarrantyFramesetSku
          ) {
            this.orderData = data.data;
            if (!this.bikesOrderUpdate && this.orderData.bikesOrderCode) {
              this.bikesOrderUpdate = this.orderData.bikesOrderCode;
              this.orderTypeMapping.bikes.hasOrderUpdate = true;
            }
            if (!this.partsOrderUpdate && this.orderData.partsOrderCode) {
              this.partsOrderUpdate = this.orderData.partsOrderCode;
              this.orderTypeMapping.parts.hasOrderUpdate = true;
            }
            if (!this.customWaterBottlesOrderUpdate && this.orderData.customWaterBottlesOrderCode) {
              this.customWaterBottlesOrderUpdate = this.orderData.customWaterBottlesOrderCode;
              this.orderTypeMapping.customWaterBottles.hasOrderUpdate = true;
            }
            if (!this.projectOneBikesOrderUpdate && this.orderData.projectOneBikesOrderCode) {
              this.projectOneBikesOrderUpdate = this.orderData.projectOneBikesOrderCode;
              this.orderTypeMapping.projectOneBikes.hasOrderUpdate = true;
            }
            if (!this.projectOneWarrantyFramesetOrderUpdate && this.orderData.projectOneWarrantyFramesetSku) {
              this.projectOneWarrantyFramesetOrderUpdate = this.orderData.projectOneWarrantyFramesetSku;
              this.orderTypeMapping.projectOneWarrantyFrameset.hasOrderUpdate = true;
            }
            this.setMessages();
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => (this.loading = false));
    },

    setMessages() {
      this.tabSelected ? this.setMessagesTabChange() : this.setMessagesFromApi();
    },

    setMessagesTabChange() {
      if (this.tabSelected === 'parts') {
        if (!this.partsOrderUpdate && !this.asyncOrderProcessing) {
          this.orderFailed();
        } else {
          this.emptyReturnError = false;
          this.orderSuccessful(this.partsOrderUpdate);
        }
      }
      if (this.tabSelected === 'bikes') {
        if (!this.bikesOrderUpdate && !this.asyncOrderProcessing) {
          this.orderFailed();
        } else {
          this.emptyReturnError = false;
          this.orderSuccessful(this.bikesOrderUpdate);
        }
      }
      if (this.tabSelected === 'customWaterBottles') {
        if (!this.customWaterBottlesOrderUpdate && !this.asyncOrderProcessing) {
          this.orderFailed();
        } else {
          this.emptyReturnError = false;
          this.orderSuccessful(this.customWaterBottlesOrderUpdate);
        }
      }
      if (this.tabSelected === 'projectOne') {
        if (
          !this.projectOneBikesOrderUpdate &&
          !this.projectOneWarrantyFramesetOrderUpdate &&
          !this.asyncOrderProcessing
        ) {
          this.orderFailed();
        } else if (this.projectOneBikesOrderUpdate) {
          this.emptyReturnError = false;
          this.orderSuccessful(this.projectOneBikesOrderUpdate);
        } else if (this.projectOneWarrantyFramesetOrderUpdate) {
          this.emptyReturnError = false;
          this.orderSuccessful(this.projectOneWarrantyFramesetOrderUpdate);
        }
      }
    },

    //Necessary for fast returns, sometimes data comes back before tabSelected is loaded.
    setMessagesFromApi() {
      if (this.orderTypeMapping.bikes?.isInCartData) {
        if (this.orderTypeMapping.bikes?.hasOrderUpdate) {
          this.orderSuccessful(this.bikesOrderUpdate);
        } else if (!this.orderTypeMapping.bikes?.hasOrderUpdate && !this.asyncOrderProcessing) {
          this.orderFailed();
        }
      } else if (this.orderTypeMapping.parts?.isInCartData) {
        if (this.orderTypeMapping.parts?.hasOrderUpdate) {
          this.orderSuccessful(this.partsOrderUpdate);
        } else if (!this.orderTypeMapping.parts?.hasOrderUpdate && !this.asyncOrderProcessing) {
          this.orderFailed();
        }
      } else if (this.orderTypeMapping.customWaterBottles?.isInCartData) {
        if (this.orderTypeMapping.customWaterBottles?.hasOrderUpdate) {
          this.orderSuccessful(this.customWaterBottlesOrderUpdate);
        } else if (!this.orderTypeMapping.customWaterBottles?.hasOrderUpdate && !this.asyncOrderProcessing) {
          this.orderFailed();
        }
      } else if (this.orderTypeMapping.projectOneBikes?.isInCartData) {
        if (this.orderTypeMapping.projectOneBikes?.hasOrderUpdate) {
          this.orderSuccessful(this.projectOneBikesOrderUpdate);
        } else if (this.orderTypeMapping.projectOneWarrantyFrameset?.hasOrderUpdate) {
          this.orderSuccessful(this.projectOneWarrantyFramesetOrderUpdate);
        } else if (!this.orderTypeMapping.projectOneBikes?.hasOrderUpdate && !this.asyncOrderProcessing) {
          this.orderFailed();
        }
      } else {
        this.orderFailed();
        console.error('Unable to set order number messages');
      }
    },

    orderSuccessful(orderNumber) {
      this.orderNumber = orderNumber;
    },

    orderFailed() {
      this.orderNumber = '';
      this.emptyReturnError = true;
    },

    displayUnavailableItemsWarnings() {
      if (!isEmpty(this.unavailableItems)) {
        this.unavailableItems.forEach((item) => {
          setTimeout(() => {
            return this.$notify({
              type: 'warning',
              message: this.skuNotAvailableText.replace('{0}', item),
            });
          }, 1000);
        });
      }
    },

    updateShippingDate(payload) {
      if (
        payload.partType === 'bikes' &&
        this.cartData.bikes.orderDetails &&
        this.cartData.bikes.orderDetails.shipmentInfo
      ) {
        this.cartData.bikes.orderDetails.shipmentInfo.deliveryDate = payload.value;
      } else if (
        payload.partType === 'parts' &&
        this.cartData.parts.orderDetails &&
        this.cartData.parts.orderDetails.shipmentInfo
      ) {
        this.cartData.parts.orderDetails.shipmentInfo.deliveryDate = payload.value;
      }
    },

    changeGridTab(tab) {
      this.tabSelected = tab;

      $('html, body').animate(
        {
          scrollTop: $(this.$refs.checkoutTabs.$el).offset().top - 150,
        },
        500
      );
    },

    // Show shipment warning if there is more than 1 warehouse
    showShipmentWarning(type) {
      if (this.checkoutStep === 'orderReview' || this.checkoutStep === 'confirmation') return false;

      if (type === 'bikes') {
        return get(this, 'cartData.bikes.warehouses', []).length > 1;
      } else if (type === 'parts') {
        return get(this, 'cartData.parts.warehouses', []).length > 1;
      } else {
        return false;
      }
    },

    // Handles the data update to the BE when the user navigates away from Shipments page
    handleShipmentsBeforeUnload(successURL) {
      axios
        .put('/checkout-b2b/api/shipments', this.compileData())
        .then((response) => {
          let responseError = get(response, 'data.meta.feedback.message.basePropertyKey', null);
          if (responseError === 'checkout.B2B.unableSaveSelections') {
            this.showSaveWarning = true;
          } else {
            window.location = successURL;
          }
        })
        .catch((error) => console.log(error));
    },

    // Handles the data update to the BE when the user navigates away from Shipments page
    performCartValidation() {
      axios
        .post('/checkout-b2b/api/validateCart', this.compileData())
        .then((response) => {
          let responseError = get(response, 'data.meta.feedback.message.basePropertyKey', null);
          if (responseError === 'checkout.B2B.cartValidationFailure') {
            this.showCartValidationWarning = true;
            $('.place-order-button').prop('disabled', true);
          }
        })
        .catch((error) => console.log(error));
    },

    compileData() {
      let compiledData = {};

      let bikeCompiledData = this.getCompiledPartType(this.cartData.bikes);
      let partsCompiledData = this.getCompiledPartType(this.cartData.parts);
      let customWaterBottlesCompiledData = this.getCompiledPartType(this.cartData.customWaterBottles);
      let projectOneBikesCompiledData = this.getCompiledPartType(this.cartData.projectOneBikes);

      if (bikeCompiledData) compiledData.bikes = bikeCompiledData;
      if (partsCompiledData) compiledData.parts = partsCompiledData;
      if (customWaterBottlesCompiledData) compiledData.customWaterBottles = customWaterBottlesCompiledData;
      if (projectOneBikesCompiledData) compiledData.projectOneBikes = projectOneBikesCompiledData;

      return compiledData;
    },

    getCompiledPartType(partData) {
      if (isEmpty(partData) || isEmpty(partData.orderDetails)) return null;

      let deliveryDate = get(partData, 'orderDetails.shipmentInfo.deliveryDate', null);

      if (!deliveryDate) {
        deliveryDate = this.getTodaysDate();
      }
      return {
        customerPO: partData.orderDetails.customerPO,
        portofDelivery: partData.orderDetails.portofDelivery,
        holdNote: partData.orderDetails.holdNote,
        holdValue: partData.orderDetails.holdValue,
        holdCode: partData.orderDetails.holdCode,
        selectedShipping: partData.orderDetails.selectedShipping,
        deliveryDate: this.getISODeliveryDate(deliveryDate),
        warehouses: this.getCompiledWarehouseData(partData.warehouses),
      };
    },

    // this method will return current date in yyyy-MM-dd format.
    getTodaysDate() {
      var currentDate = new Date();
      var yyyy = currentDate.getFullYear().toString();
      var mm = (currentDate.getMonth() + 1).toString(); // getMonth() is zero-based
      var dd = currentDate.getDate().toString();

      return yyyy + '-' + (mm[1] ? mm : '0' + mm[0]) + '-' + (dd[1] ? dd : '0' + dd[0]);
    },

    getCompiledWarehouseData(warehouses) {
      if (isEmpty(warehouses)) return [];

      return map(warehouses, (warehouse) => {
        return {
          id: warehouse.id,
          isBackOrdered: this.getBackOrderStatus(warehouse),
          items: map(warehouse.entries, (item) => {
            return {
              sku: item.sku,
              qty: warehouse.id === 'backOrdered' ? item.backorderedQty : item.nonBackorderedQty,
              isAllocated: item.isAllocated,
              productRecipeID: item.productRecipeID,
              p1ConfigurationId: item?.p1ConfigurationId,
            };
          }),
        };
      });
    },

    getFreightWarehouseData(warehouses) {
      if (isEmpty(warehouses)) return [];

      return map(warehouses, (warehouse) => {
        return {
          id: warehouse.id,
          isBackOrdered: this.getBackOrderStatus(warehouse),
        };
      });
    },

    getBackOrderStatus({id, isPrimary, isBackOrdered}) {
      if (isPrimary) {
        return false;
      } else if (id === 'backOrdered') {
        return true;
      } else {
        return isBackOrdered;
      }
    },

    getISODeliveryDate(date) {
      if (!date) return null;

      let deliveryDate = new Date(date);

      return deliveryDate.toISOString();
    },

    enableShipmentsSubmitListeners() {
      $('.js-shipments-listener').click(
        $.proxy(function(event) {
          event.preventDefault();
          this.$validator.validateAll().then((result) => {
            if (!result) {
              const firstErrorFieldName = Object.keys(this.$validator.errors.collect())[0];
              if (firstErrorFieldName.startsWith('parts')) {
                this.changeGridTab('parts');
              } else if (firstErrorFieldName.startsWith('bikes')) {
                this.changeGridTab('bikes');
              }
              return;
            }
            this.handleShipmentsBeforeUnload(event.currentTarget.href);
          });
        }, this)
      );
    },

    getFreightObject() {
      let data = {bikes: {}, parts: {}};

      data['isAllocatedItemsPage'] = false;
      if (this.cartData.bikes && this.cartData.bikes.orderDetails) {
        data.bikes['warehouses'] = this.getCompiledWarehouseData(this.cartData.bikes.warehouses);
        data.bikes['selectedShipping'] = this.cartData.bikes.orderDetails.selectedShipping;
      }

      if (this.cartData.parts && this.cartData.parts.orderDetails) {
        data.parts['warehouses'] = this.getCompiledWarehouseData(this.cartData.parts.warehouses);
        data.parts['selectedShipping'] = this.cartData.parts.orderDetails.selectedShipping;
      }
      return data;
    },

    recalculateFreight(cartType) {
      axios
        .put('/cart/freight', this.getFreightObject())
        .then(() => {
          this.updateShippingCart().finally(() => {
            this.calculatedFreight[cartType] = true;
          });
        })
        .catch((error) => console.log(error));
    },

    updateShippingCart() {
      return axios
        .get(this.endpoint)
        .then(({data}) => {
          this.cartData.cartSummary.bikes = data.data.cartSummary.bikes;
          this.cartData.cartSummary.parts = data.data.cartSummary.parts;
          this.validateCartData();
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => (this.loading = false));
    },

    loadCart() {
      return axios
        .get(this.endpoint)
        .then(({data}) => {
          this.cartData = data.data;
          this.validateCartData();
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => (this.loading = false));
    },

    updateItemBTOData(payload) {
      if (payload) {
        const warehouse = this.bikes.warehouses.find((warehouse) => warehouse.id === payload.warehouseId);
        const item = warehouse.entries.find((item) => item.sku === payload.sku);

        item.lineNote = payload.lineNote;
        item.alreadySoldToConsumer = payload.alreadySoldToConsumer;
      }
    },

    showCheckoutSuccessMessage() {
      return (
        Object.keys(this.allocatedItemsExistenceMap).length > 0 && this.allocatedItemsExistenceMap[this.tabSelected]
      );
    },
    showProjectOneWarrantyFramesetSuccessMessage() {
      return (
        Object.keys(this.allocatedItemsExistenceMap).length > 0 &&
        !this.allocatedItemsExistenceMap[this.tabSelected] &&
        this.orderNumber &&
        this.projectOneWarrantyFramesetOrderUpdate
      );
    },
  },
};
</script>
